import { type ChangeEvent, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminQueries,
  createNewTenantRecord,
  type Tenant,
  updateExistingTenantRecord,
} from "@/api/api";
import { useToast } from "@/hooks/use-toast";
import { Role } from "@/components/AuthorizationProvider";
import { type Persona, usePersonasQuery } from "@/api/personas";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusIcon, RefreshCw } from "lucide-react";
import PersonaCards from "@/components/PersonaCards";
import { useAuth } from "@/hooks/use-auth";

interface TenantTabContentProps {
  onUserSelect: (username: string) => void;
}

export function TenantTabContent({ onUserSelect }: TenantTabContentProps) {
  const queryClient = useQueryClient();
  const { hasAtLeastRole, username } = useAuth();
  const { toast } = useToast();

  // State
  const [selectedTenant, setSelectedTenant] = useState<
    Tenant | null | undefined
  >(null);
  const [tenantsUsernames, setTenantsUsernames] = useState<string[]>([]);
  const [isNewTenant, setIsNewTenant] = useState(false);
  const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);

  // Queries
  const { data: tenants = [] } = useQuery(adminQueries.tenants());
  const { data: personas = [] } = usePersonasQuery();

  const isSuperAdmin = hasAtLeastRole(Role.SuperAdmin);

  const resetState = () => {
    setSelectedTenant(null);
    setFilteredPersonas([]);
  };

  const handleRefreshData = () => {
    queryClient.invalidateQueries(adminQueries.tenants());
  };

  const handleTenantSelect = async (tenantName: string) => {
    resetState();
    const tenant = tenants.find((t) => t.tenant_name === tenantName);
    setSelectedTenant(tenant);
    setIsNewTenant(false);

    try {
      const allUsers = await queryClient.ensureQueryData(adminQueries.users());
      const tenantUsers = allUsers.filter(
        (user) => user.qstarlabs_tenant === tenant!.tenant_name,
      );
      setTenantsUsernames(tenantUsers.map((t) => t.username));

      const filteredPersonas = personas.filter(
        (p) => p.qstarlabs_tenant === tenant!.tenant_name,
      );
      setFilteredPersonas(filteredPersonas);
    } catch (error) {
      console.error("Error loading tenant data:", error);
      toast({
        variant: "destructive",
        description: "Failed to load tenant data. Please try again.",
      });
    }
  };

  const handleNewTenant = () => {
    resetState();
    setSelectedTenant({
      tenant_name: "",
      company: "",
      owner: username!,
      logo_url: "",
    });
    setTenantsUsernames([username!]);
    setIsNewTenant(true);
  };

  const handleTenantFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSelectedTenant((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const saveTenant = async () => {
    try {
      if (isNewTenant) {
        await createNewTenantRecord(selectedTenant);
      } else {
        await updateExistingTenantRecord(selectedTenant);
      }

      toast({
        title: "Success",
        description: "Tenant saved successfully!",
      });

      queryClient.invalidateQueries(adminQueries.tenants());
      setIsNewTenant(false);
    } catch {
      toast({
        variant: "destructive",
        description: "Error saving tenant. Please try again.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <CardHeader className="px-0 pt-0">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-semibold text-primary">
                Tenant Management
              </CardTitle>
              <CardDescription>
                Manage tenants and their associated users and agents
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handleRefreshData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              {isSuperAdmin && (
                <Button onClick={handleNewTenant} className="gap-1">
                  <PlusIcon className="h-5 w-5" />
                  Add New Tenant
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          <div className="space-y-6">
            {/* Tenant Selection */}
            <div className="grid grid-cols-1 gap-4">
              <Label className="flex flex-col gap-y-2">
                Select Tenant
                <Select
                  value={selectedTenant ? selectedTenant.tenant_name : ""}
                  onValueChange={handleTenantSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(tenants) && tenants.length > 0 ? (
                      tenants.map((tenant) => (
                        <SelectItem
                          key={tenant.tenant_name}
                          value={tenant.tenant_name}
                        >
                          {tenant.tenant_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem disabled value={null!}>
                        No tenants available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Label>
            </div>

            {/* Tenant Form */}
            {selectedTenant && isSuperAdmin && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Tenant Name</Label>
                  <Input
                    name="tenant_name"
                    value={selectedTenant.tenant_name || ""}
                    readOnly={!isNewTenant}
                    onChange={handleTenantFieldChange}
                  />
                </div>

                <div>
                  <Label>Company</Label>
                  <Input
                    name="company"
                    value={selectedTenant.company || ""}
                    onChange={handleTenantFieldChange}
                  />
                </div>

                <div>
                  <Label>Owner</Label>
                  <Select
                    value={selectedTenant.owner || ""}
                    onValueChange={(value) =>
                      handleTenantFieldChange({
                        // @ts-expect-error wrong type
                        target: { name: "owner", value },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenantsUsernames.map((username) => (
                        <SelectItem key={username} value={username}>
                          {username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Logo URL</Label>
                  <Input
                    name="logo_url"
                    value={selectedTenant.logo_url || ""}
                    onChange={handleTenantFieldChange}
                  />
                </div>

                <div className="md:col-span-2 flex justify-end">
                  <Button onClick={saveTenant}>Save Tenant</Button>
                </div>
              </div>
            )}

            {/* Users and Personas */}
            {selectedTenant && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t">
                <div>
                  <Label className="mb-4 block">Users in Tenant</Label>
                  <div className="space-y-2">
                    {tenantsUsernames.map((username) => (
                      <Link
                        key={username}
                        to="#"
                        onClick={() => onUserSelect(username)}
                        className="text-primary hover:underline block"
                      >
                        {username}
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-4 block">Tenant Agents</Label>
                  <PersonaCards
                    personas={filteredPersonas}
                    onSelectPersona={(id) =>
                      console.log(`Selected Agent ID: ${id}`)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
